import { pickT } from "@repo/shared";
import type { FlowDocumentUrlResponse } from "@repo/shared/types";
import { Download } from "lucide-react";
import { useState } from "react";
import { get, getWithToken } from "@/lib/api/_client";
import { usePublicSession } from "@/lib/context/public-session-context";
import type { FileConfig } from "@/lib/types/flow";
import { T } from "./file-renderer.i18n";

type FileRendererProps = {
  config: FileConfig;
  /** The words follow this (default en_GB), as `renderFile` passes it through. */
  language?: string;
};

/**
 * Download-styled link for the `file` flow component. The config stores the
 * document's s3Key; on click we exchange it for a short-lived presigned URL
 * (internal via session cookie, public flow via the session JWT) and open it
 * in a new tab. The tab is opened synchronously so popup blockers allow it.
 */
export function FileRenderer({ config, language }: FileRendererProps) {
  const session = usePublicSession();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const t = pickT(T, language || "en_GB");

  if (!config.s3Key) return null;

  const label = config.label || config.fileName || "Download";

  const handleClick = async () => {
    if (loading) return;
    setLoading(true);
    setError(false);

    const newTab = window.open("", "_blank");

    try {
      const response = session.isPublic
        ? await getWithToken<FlowDocumentUrlResponse>(
            `/public/flow/file/document-url/${config.s3Key}`,
            session.sessionToken,
          )
        : await get<FlowDocumentUrlResponse>(`/internal/uploads/flow-document-url/${config.s3Key}`);

      if (newTab) {
        newTab.location.href = response.url;
      } else {
        window.open(response.url, "_blank");
      }
    } catch {
      newTab?.close();
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={handleClick}
        disabled={loading}
        className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline disabled:opacity-50"
      >
        <Download className="h-4 w-4 shrink-0" />
        {label}
      </button>
      {error && <p className="text-xs text-destructive">{t.downloadFailed}</p>}
    </div>
  );
}
